import { Localization } from "OW.Remoting/Remoting/Localization";
import { DeviceInfo } from "../../../../Device/Info/DeviceInfo";
import { EnumUtils } from "../../../../Global/Base/Utils/EnumUtils";
import { Globalization, ITimeInfo } from "../../../../Global/Dom/Globalization";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Control } from "./Control";
import { ControlParams } from "./ControlInterfaces";
import { ControlUtils } from "./ControlUtils";
import { CultureDependentEditableControl } from "./CultureDependentEditableControl";

/** Trida pro TimeEdit.
    */
export class TimeEdit extends CultureDependentEditableControl {
    //#region Fields

    /**
     * Pokud je true, bude hodnota hodin v času vždy dvoučiselná (doplní se připadně nula na začatek)
     */
    public twoDigitHour: boolean = true;

    //#region Regex
    public static timeRegex = /^(:?([01]?[0-9]|2[0-3])|([01]?[0-9]|2[0-3]):([0-5][0-9])|([01]?[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9]))$/; // H | H:mm | H:mm:ss
    //#endregion

    private _listeners: any = {};
    public override innerElement: HTMLInputElement;
    private _value: Date = null;
    private _timeFormat: TimeFormat = TimeFormat.tfMin;
    private _localeTimeFormat: ITimeInfo;

    // @ts-ignore // nevyužito, ale ponecháno
    private _testPattern: RegExp;
    private _validTimePattern: RegExp;

    private _edited: boolean = false;
    //#endregion

    public get timeFormat(): TimeFormat {
        return this._timeFormat;
    }
    public set timeFormat(value: TimeFormat) {
        this._timeFormat = value;
    }

    //#region Inherited from EditableControl
    public override set controlValue(value: any) { // prijima Date
        if (value !== undefined && value !== null) { // neprazdna hodnota
            if ((value instanceof Date) && !isNaN(value.getTime())) { // spravne
                this._value = value;
                this.innerElement.value = this._formatVal(value);
            } else {
                this._value = null;
                this.innerElement.value = "";
            }
        } else { // prazdna hodnota
            this._value = null;
            this.innerElement.value = "";
            this._setGuiValid();
            this.setValid();
        }
    }

    /** Vrati obsah this.value v UTC cas. zone (tedy s prictenim timezoneOffsetu k lokalnim casu). Vsechny casy v aplikaci se totiz momentalne berou jako lokalni, kdezto JS vraci cas v UTC. */
    public override get controlValue(): any {
        //return this.formatVal(this.value);
        return this._value;
    }
    //#endregion Inherited from EditableControl
    //#endregion Properties

    //#region Constructor
    constructor(params: ControlParams) {
        super(params);
        this.element.classList.add("TimeEdit");
        this.attrName = this.getDataAttr("attrname");

        // Nastavení cultury podle elementu
        this._localeTimeFormat = this.culture && this.culture !== Globalization.actualCulture ? Globalization.getTimeFormat(this.culture) : Globalization.timeFormat;

        this._createElement();
        this._setAttrs();
        this._setPattern();
        this._setListeners();
        this._setCaptionReference(this.innerElement);

        // zpracování události pro změnu cultury (formátování)
        this.cultureChanged.add(this._cultureChangedEvent);
    }
    //#endregion Constructor

    //#region Methods
    /** zjistuje podporu nativ. inputu */
    public static supportsNativeInput(): boolean {
        if (DeviceInfo.deviceProperties.iOS || DeviceInfo.deviceProperties.blackBerry) // iOS a BB nepodporuji atribut 'step'
            return false;

        const time = createElement({ type: "input", attrs: { "type": "time" } }) as HTMLInputElement;
        time.value = "abcd"; //TODO: toto má nějaký hlubší smysl?
        return (time.getAttribute("type") === "time" && time.value === "");
    }

    /**
     * Metoda na zpracování události při změně jazyka a culture
     */
    private _cultureChangedEvent(sender: Control, _culture: string): void {
        const tedit = sender as TimeEdit;

        tedit._setPattern();

        if (tedit._value != null && !isNaN(tedit._value.getTime()))
            tedit.innerElement.value = tedit._formatVal(tedit._value);
    }

    /**
     * Základní nastavení formátovaci regularnich výrazů
     */
    private _setPattern(): void {
        // Nastavení cultury podle elementu
        this._localeTimeFormat = this.culture && this.culture !== Globalization.actualCulture ? Globalization.getTimeFormat(this.culture) : Globalization.timeFormat;

        const separator = this._localeTimeFormat.separator === "." ? "\\." : this._localeTimeFormat.separator;

        //let TIME24HOUR = new RegExp(`^(0?[0-9]|1[0-9]|2[0-4])${separator}00$`);
        //let TIME12HOUR = new RegExp(
        //    (this.localeTimeFormat.AmPmOnRight
        //        ? "^"
        //        : `((${this.localeTimeFormat.AmText}|${this.localeTimeFormat.PmText})\\s?)?`) +
        //    `(0?[1-9]|1[0-2])${separator}00` +
        //    (this.localeTimeFormat.AmPmOnRight
        //        ? `(\\s?(${this.localeTimeFormat.AmText}|${this.localeTimeFormat.PmText}))?$`
        //        : "$"), "i");
        const TIME24HOUR = new RegExp("^(0?[0-9]|1[0-9]|2[0-4])$");
        const TIME12HOUR = new RegExp(
            (this._localeTimeFormat.amPmOnRight
                ? "^"
                : `((${this._localeTimeFormat.amText}|${this._localeTimeFormat.pmText})\\s?)?`) +
            "(0?[1-9]|1[0-2])" +
            (this._localeTimeFormat.amPmOnRight
                ? `(\\s?(${this._localeTimeFormat.amText}|${this._localeTimeFormat.pmText}))?$`
                : "$"), "i");

        const TIME24HOUR_MIN = new RegExp(`^(0?[0-9]|1[0-9]|2[0-4])${separator}[0-5][0-9]$`);
        const TIME12HOUR_MIN = new RegExp(
            (this._localeTimeFormat.amPmOnRight
                ? "^"
                : `((${this._localeTimeFormat.amText}|${this._localeTimeFormat.pmText})\\s?)?`) +
            `(0?[1-9]|1[0-2])${separator}[0-5][0-9]` +
            (this._localeTimeFormat.amPmOnRight
                ? `(\\s?(${this._localeTimeFormat.amText}|${this._localeTimeFormat.pmText}))?$`
                : "$"), "i");

        const TIME24HOUR_SEC = new RegExp(`^(0?[0-9]|1[0-9]|2[0-4])${separator}[0-5][0-9]${separator}[0-5][0-9]$`);
        const TIME12HOUR_SEC = new RegExp(
            (this._localeTimeFormat.amPmOnRight
                ? "^"
                : `((${this._localeTimeFormat.amText}|${this._localeTimeFormat.pmText})\\s?)?`) +
            `(0?[1-9]|1[0-2])${separator}[0-5][0-9]${separator}[0-5][0-9]` +
            (this._localeTimeFormat.amPmOnRight
                ? `(\\s?(${this._localeTimeFormat.amText}|${this._localeTimeFormat.pmText}))?$`
                : "$"), "i");

        const VALID_TIME24 = new RegExp(`^(0?[0-9]?|1[0-9]?|2[0-4]?)${separator}?([0-5][0-9]?)?${separator}?([0-5][0-9]?)?$`);
        let VALID_TIME12 = /^(0?[1-9]?|1[0-2]?):?([0-5]\d?)?:?([0-5]\d?)?$/i;
        //let VALID_TIME12 = /^(0?[1-9]?|1[0-2]?):?([0-5]\d?)?:?([0-5]\d?)?\s?((A|P)(M)?)?$/i;

        if (this._localeTimeFormat.useAmPm) {
            let valid_time12_str = `(0?[1-9]?|1[0-2]?)${separator}?([0-5]\\d?)?${separator}?([0-5]\\d?)?`;
            let valid_time12_ampm_text = "(";
            for (let i = 0, len = this._localeTimeFormat.pmText.length; i < len; i++) {
                const char = this._localeTimeFormat.pmText[i];
                valid_time12_ampm_text += `${char}${i === 0 ? "" : "?"}`;
            }
            valid_time12_ampm_text += "|";
            for (let i = 0, len = this._localeTimeFormat.amText.length; i < len; i++) {
                const char = this._localeTimeFormat.amText[i];
                valid_time12_ampm_text += `${char}${i === 0 ? "" : "?"}`;
            }
            valid_time12_ampm_text += ")?";

            if (this._localeTimeFormat.amPmOnRight)
                valid_time12_str = `^${valid_time12_str}\\s?${valid_time12_ampm_text}$`;
            else
                valid_time12_str = `^${valid_time12_ampm_text}\\s?${valid_time12_str}$`;

            VALID_TIME12 = new RegExp(valid_time12_str, "i");
        }

        if (this._localeTimeFormat.hour12) {
            this._validTimePattern = VALID_TIME12;
            switch (this._timeFormat) {
                case TimeFormat.tfHour:
                    this._testPattern = TIME12HOUR;
                    break;
                case TimeFormat.tfMin:
                    this._testPattern = TIME12HOUR_MIN;
                    break;
                case TimeFormat.tfSec:
                    this._testPattern = TIME12HOUR_SEC;
                    break;
            }
        } else {
            this._validTimePattern = VALID_TIME24;
            switch (this._timeFormat) {
                case TimeFormat.tfHour:
                    this._testPattern = TIME24HOUR;
                    break;
                case TimeFormat.tfMin:
                    this._testPattern = TIME24HOUR_MIN;
                    break;
                case TimeFormat.tfSec:
                    this._testPattern = TIME24HOUR_SEC;
                    break;
            }
        }
    }

    private _createElement(): void {
        const input = this.innerElement = createElement({ type: "input" }) as HTMLInputElement;
        if (this._placeholderLocKey) {
            Localization.addLocTxtData(input, this._placeholderLocKey, "placeholder");
        }
        this.element.appendChild(input);
    }

    /**
     * Nastavení nativních události
     */
    private _setListeners(): void {
        const f_focusTimeout = () => {
            const lCurosPos = ControlUtils.getInputSelection(this.innerElement).start;

            if (this._value) {
                ControlUtils.setCaret(this.innerElement, lCurosPos);
            }
        };

        this.innerElement.addEventListener("blur", this._listeners["blur"] = () => {
            this.validate();
            this._afterBlur(); // upravy formátování uživatelského vstupu
            this.element.classList.remove("focused");
        });

        this.innerElement.addEventListener("focus", this._listeners["focus"] = () => {
            // timeout, protoze dochazi k prechodu do editace pri kliku do pole, pokud bude pole po timeoutu v editaci dojde ke zpracovani
            self.setTimeout(() => {
                if (this.isEditing)
                    f_focusTimeout();
            }, 150);
            if (this.enabled)
                this.element.classList.add("focused");
        });

        //Uživatele bysme měli nechat napsat jakoukoliv hodnotu chce a pak mu to zvalidovat
        this.innerElement.addEventListener("keyup", this._listeners["keyup"] = () => {
            if (!this.isEditing)
                return;

            if (this._checkTypingValue()) {
                this._edited = true;
            }
        });
    }

    public override validate(): boolean {
        const val = this.innerElement.value;
        if (val === "") {
            this._setGuiValid();
            this.setValid();
            return this.isValid;
        }

        if (!this._checkTypingValue()) {
            this._setGuiInvalid();
            this.setInvalid("GUI-214428");
        } else {
            this._setGuiValid();
            this.setValid();
        }

        return this.isValid;
    }

    /**
     * Kontrola hodnoty inputu prováděná při psani
     */
    private _checkTypingValue(): boolean {
        const val = this.innerElement.value;
        if (val === "")
            return true;
        return this._validTimePattern.test(val);
    }

    /**
     * Metoda volana pri opusteni inputu, na doplneni hodnot
     */
    private _afterBlur(): void {
        const val = this.innerElement.value;
        if (!this._checkTypingValue()) {
            this._value = null;
            return;
        }

        if (val === "") {
            this._value = null;
        } else {
            const newTime = this._parseTime(val);
            this._value = newTime;
            this.innerElement.value = this._formatVal(newTime);
        }
        if (this._edited && this._enabled)
            this.valueChanged.trigger(this, { Sender: this });
        this._edited = false;
    }

    /**
     * Parsuje hodnotu ze vstupu
     * @example Příklad validního uživatelského vstupu je 1 => 1:00:00, 1001 => 10:01:00, 1:1 => 1:01:00, ...
     * @param val uživatelský vstup
     */
    private _parseTime(val: string): Date {
        const time: string[] = ["", "", ""];
        const min_num = this._localeTimeFormat.hour12 ? 1 : 2;
        const max_next_num = this._localeTimeFormat.hour12 ? 2 : 4;
        let fNum = 0;
        // temp funkce na zpracovani stringu jako čisla
        const f_getNum = (n: string): number => {
            const num = parseInt(n);
            if (isNaN(num))
                return 0;
            else
                return num;
        };

        for (let i = 0, j = 0, z = 0, lng = val.length; i < lng; i++) {
            if (val[i] === this._localeTimeFormat.separator) {
                z++;
                j = 0;
                continue;
            }
            if (j === 2) {
                j = 0;
                z++;
            }
            if (z > 2)
                break;

            if (/\d/.test(val[i])) {
                // pokud obsahuje na prvnim miste cislo vetsi nez maximalni doplni na zacatek nulu, jinak to k datu na zacatku pricitalo dny
                const pNum = parseInt(val[i]);
                if (z === 0 && j === 0) {
                    fNum = pNum;
                    if (pNum > min_num) {
                        time[z] += "0";
                        j++;
                    }
                }
                // pokud predchozi cislo obsazhuje maximalni cislo zadane do dne a dalsi cislo obsahuje vetsi nez koncove cislo dne, je pridana na zacatek 0
                // priklad: pokud bych zadal (ve 24 hod formatu) 25, predchozi podminkou by to proslo, ale datum se presto o jeden den zvedl
                if (z === 0 && j === 1) {
                    if (fNum === min_num && pNum > max_next_num) {
                        time[z] = `0${time[z]}`;
                        j = 0;
                        z++;
                    }
                }
                time[z] += val[i];
                j++;
            }
        }

        // kontrola na zadani hodnoty PM
        let hour = f_getNum(time[0]);
        let rtext: string;
        if (this._localeTimeFormat.amPmOnRight)
            rtext = `.+\\s?${this._localeTimeFormat.pmText}$`;
        else
            rtext = `${this._localeTimeFormat.pmText}\\s?.+$`;
        const reg = new RegExp(rtext, "i");

        // prevadeni na spravne hodnoty PM
        if (this._localeTimeFormat.useAmPm) {
            ///.+\s?PM$/i.test(val)) {
            if (reg.test(val)) {
                if (hour !== 12)
                    hour += 12;
            } else if (hour === 12) { // pokud je 12 hodinovy format, cas je v AM a je roven 12, je to upraveno na 0, dochazelo k prepisu na AM a PM
                hour = 0;
            }
        }
        if (!this._value)
            return new Date(9999, 0, 1, hour, f_getNum(time[1]), f_getNum(time[2]));
        else
            return new Date(this._value.getFullYear(), this._value.getMonth(), this._value.getDate(), hour, f_getNum(time[1]), f_getNum(time[2]));
    }

    /**
     * Vráti naformátovaný čas podle aktuální kultůry
     * @param val Čas podle kterého se bude formátovat
     */
    private _formatVal(val: Date): string {
        //let printDate = new Date(val.getTime());
        // TODO: (21. 3. 2017) Kvůli chybě v nativní funkci toLocaleTimeString se při zadání data menšího než "25.10.1891" a následného zadaní času snižuje čas. např: 1.1.1890 12:00:00 - čas se zmenší na 11:57:44
        const printDate = new Date(2000, 1, 1, val.getHours(), val.getMinutes(), val.getSeconds(), val.getMilliseconds());
        const opt: any = { hour: "2-digit", minute: "2-digit" };

        if (!this.twoDigitHour)
            opt.hour = "numeric";

        if (this._timeFormat === TimeFormat.tfHour) {
            //printDate.setMinutes(0);
            opt.minute = undefined;
        } else if (this._timeFormat === TimeFormat.tfSec) {
            opt.second = "2-digit";
        }

        //return printDate.toLocaleTimeString(this.Culture ? this.Culture : Globalization.ActualCulture, opt);
        let finalDate = printDate.toLocaleTimeString(this.culture ? this.culture : Globalization.actualCulture, opt);

        const t_hour = printDate.toLocaleTimeString(this.culture ? this.culture : Globalization.actualCulture, { hour: "2-digit" });
        // TODO: (10. 2. 2016) v opt hour: 2-digit funguje spravne jen v Edge, v ostatnich prohlížečích se zobrazí jedno číslo, byla k tomu třeba udělat berlička, až to bude fungovat odstranit
        if (this.twoDigitHour && t_hour.length < 2)
            finalDate = `0${finalDate}`;

        return finalDate;
    }

    private _setAttrs(): void {
        this.innerElement.type = "text"; // default; type="text" kvuli zadavani ':'
        this.innerElement.maxLength = 11;
        const tFromat = EnumUtils.getEnumValueFromName(XGenTimeFormat, this.getDataAttr("timeFormat"), true) || this.getDataAttr("timeFormat");
        this._timeFormat = EnumUtils.getEnumValueFromName(TimeFormat, tFromat) || TimeFormat.tfMin; // tfMin je defaultni
    }

    public override dispose(): void {
        this.innerElement.removeEventListener("focus", this._listeners["focus"]);
        this.innerElement.removeEventListener("keyup", this._listeners["keyup"]);
        this.innerElement.removeEventListener("blur", this._listeners["blur"]);
        this.cultureChanged.remove(this._cultureChangedEvent);
        super.dispose();
    }

    //#endregion
}

export enum TimeFormat {
    tfHour = 1,
    tfMin = 2,
    tfSec = 3,
}

/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
export enum XGenTimeFormat {
    T = "tfSec",
    t = "tfMin",
    F = "tfSec",
    f = "tfMin",
    G = "tfSec",
    g = "tfMin",
    O = "tfSec",
    o = "tfSec",
    r = "tfSec",
    R = "tfSec",
    s = "tfSec",
    u = "tfSec",
    U = "tfSec",
}
